import { apiUrl, isNative } from './config'

/**
 * Anmeldung/Verknüpfung über Authentik (OIDC, ADR-082) — Web-Hilfen.
 *
 * **Nur im Web.** Der Flow ist eine Kette von Vollseiten-Weiterleitungen
 * (App → Authentik → App) und lebt vom HttpOnly-Cookie. In der nativen
 * Capacitor-Hülle wäre dafür ein System-Browser (ASWebAuthenticationSession /
 * Custom Tab) plus Deep-Link-Rückweg nötig; das ist für v1 bewusst
 * ausgeklammert. Native Clients melden sich weiterhin per Name +
 * Passwort/PIN an und bekommen ihren Bearer-Token wie bisher.
 */
export const oidcAvailableInThisClient = !isNative

/** Volle URL zum Start des Flows (Vollseiten-Navigation, kein fetch). */
export function oidcStartUrl(mode: 'login' | 'connect'): string {
  return apiUrl(`/api/auth/oidc/start?mode=${mode}`)
}

/** Fehlercodes des Servers (`?oidc_error=…`) in deutsche Klartexte übersetzen. */
export function oidcErrorText(code: string): string {
  switch (code) {
    case 'not_linked':
      return 'Dieses Authentik-Konto ist mit keinem FamilyNest-Profil verknüpft. Melde dich mit Name und Passwort/PIN an und verbinde das Konto anschließend in deinem Profil.'
    case 'sub_taken':
      return 'Dieses Authentik-Konto ist bereits mit einem anderen FamilyNest-Profil verbunden.'
    case 'session_required':
      return 'Zum Verbinden musst du in FamilyNest angemeldet sein.'
    case 'connect_forbidden':
      return 'Das Wand-Tablet-Profil kann nicht mit einem persönlichen Konto verbunden werden.'
    case 'too_many_attempts':
      return 'Zu viele Versuche. Bitte in einigen Minuten noch einmal probieren.'
    case 'state_expired':
    case 'state_mismatch':
      return 'Die Anmeldung hat zu lange gedauert oder wurde unterbrochen. Bitte noch einmal starten.'
    case 'provider_unreachable':
    case 'issuer_mismatch':
      return 'Authentik ist gerade nicht erreichbar. Der Login mit Name und Passwort/PIN funktioniert weiterhin.'
    default:
      return 'Die Anmeldung über Authentik hat nicht geklappt. Der Login mit Name und Passwort/PIN funktioniert weiterhin.'
  }
}

/**
 * Liest `?oidc_error=` / `?oidc=connected` aus der URL und räumt sie danach aus
 * der Adresszeile (History-Replace), damit ein Reload die Meldung nicht wiederholt.
 */
export function takeOidcResult(): { error: string | null; connected: boolean } {
  if (typeof window === 'undefined') return { error: null, connected: false }
  const params = new URLSearchParams(window.location.search)
  const error = params.get('oidc_error')
  const connected = params.get('oidc') === 'connected'
  if (!error && !connected) return { error: null, connected: false }
  params.delete('oidc_error')
  params.delete('oidc')
  const rest = params.toString()
  window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash)
  return { error: error ? oidcErrorText(error) : null, connected }
}
